/**
 * Registry central de navegação — ADR-014. Cobre o cluster admin "Acessos &
 * Permissões" (Fase 0), os 7 módulos do Dashboard Launcher (Fase 3) e, a
 * partir do adendo de 2026-07-28, o 1º módulo da zona user ("Pool de
 * Hotelaria" — Visão Geral/Cotas e Unidades/Ocupação).
 *
 * Zero dependência de react/react-dom (mesmo motivo de root-registry.ts) —
 * pode ser importado estaticamente por main.ts sem entrar no bundle eager
 * puxando React.
 *
 * Sidebar (nav-config.ts) e breadcrumbs (main.ts) leem daqui. Não substitui
 * o roteador (`main.ts::routes`) — `path` aqui é a representação de
 * navegação/exibição; a mecânica de match de rota continua em main.ts.
 */

import { codename } from "./resource-catalog";

export type RoleCtx = { isSuperadmin: boolean };

/**
 * Zona de navegação. Cada `NavEntry` pertence a exatamente uma — invariante:
 * toda cadeia de `breadcrumbParent` deve permanecer dentro da mesma `zone`;
 * não há (nem deve haver) cadeia cruzando `user`↔`admin`, já que cada zona
 * resolve para uma raiz de breadcrumb diferente (`ROOTS`, abaixo).
 */
export type NavZone = "user" | "admin";

export interface NavEntry {
  id: string;
  title: string;
  path: string | ((ctx: RoleCtx) => string);
  /**
   * obrigatório de propósito: um campo opcional com fallback silencioso para
   * uma zona default é o tipo de ponto de sincronização implícito que o
   * ADR-014 existe para eliminar (mesma lógica do achado de `routeKeys` na
   * Fase 0) — melhor o build quebrar por entrada esquecida do que resolver
   * errado em silêncio.
   */
  zone: NavZone;
  /** id de outro NavEntry — resolve a cadeia de breadcrumb recursivamente. */
  breadcrumbParent?: string;
  perm?: string;
  superadminOnly?: boolean;
  /** aparece como item de menu na SideNav? */
  sidebar?: boolean;
  /**
   * aparece como card no Dashboard Launcher (ADR-014, Fase 3)? Não é sinônimo
   * de `sidebar` — existem itens com `sidebar: true` sem card (ex.: Auditoria
   * de Acesso, Documentação da API), evidência levantada no adendo da Fase 3.
   * Não existe Launcher na zona user hoje — entradas `zone: "user"` omitem
   * este campo.
   */
  launcher?: boolean;
  icon?: string;
  /** chaves de rota (main.ts) que marcam este item como ativo — mesma semântica de nav-config.ts. */
  routeKeys: string[];
}

const ROOTS: Record<NavZone, BreadcrumbItem> = {
  admin: { label: "Dashboard", href: "/painel/admin/" },
  user: { label: "Dashboard", href: "/app/dashboard" },
};

export const navRegistry: NavEntry[] = [
  {
    id: "access-permissions-section",
    title: "Acessos & Permissões",
    path: "/painel/admin/permissoes/",
    zone: "admin",
    sidebar: false,
    routeKeys: [],
  },
  {
    id: "permission-catalog",
    title: "Permissões (avançado)",
    path: "/painel/admin/permissoes/catalogo/",
    zone: "admin",
    breadcrumbParent: "access-permissions-section",
    superadminOnly: true,
    sidebar: true,
    launcher: true,
    icon: "lock",
    routeKeys: ["perm-catalogo", "admin-catalogo"],
  },
  {
    id: "permission-profiles",
    title: "Perfis de Acesso",
    path: (ctx) => (ctx.isSuperadmin ? "/painel/admin/permissoes/perfis/" : "/painel/admin/perfis/"),
    zone: "admin",
    breadcrumbParent: "access-permissions-section",
    perm: codename("gestao.admin.grupos", "view"),
    sidebar: true,
    launcher: true,
    icon: "lock",
    routeKeys: ["perm-grupos", "admin-grupos"],
  },
  {
    id: "access-audit",
    title: "Auditoria de Acesso",
    path: (ctx) => (ctx.isSuperadmin ? "/painel/admin/permissoes/acesso/" : "/painel/admin/acesso/"),
    zone: "admin",
    breadcrumbParent: "access-permissions-section",
    perm: codename("gestao.admin.grupos", "view"),
    sidebar: true,
    icon: "lock",
    routeKeys: ["perm-acesso", "admin-acesso"],
  },
  {
    id: "users",
    title: "Usuários",
    path: (ctx) => (ctx.isSuperadmin ? "/painel/admin/permissoes/usuarios/" : "/painel/admin/usuarios/"),
    zone: "admin",
    breadcrumbParent: "access-permissions-section",
    perm: codename("gestao.admin.usuarios", "view"),
    sidebar: true,
    launcher: true,
    icon: "lock",
    routeKeys: ["perm-usuarios", "admin-usuarios"],
  },
  {
    // Crumb intermediário do grupo "Configurações" (nav-config.ts) — sem item
    // próprio de sidebar (sidebar:false), só para o breadcrumb de
    // "integrations" não pular direto de "Dashboard" pro título da página
    // (mesmo padrão de "access-permissions-section"). Renomeado de
    // "Configuração" (singular) pelo plano "Mídia e uploads por módulo"
    // (Fase 5, 11-09-2026) — grupo movido para a última posição da sidebar
    // (nav-config.ts).
    id: "configuracao-section",
    title: "Configurações",
    path: "/painel/admin/integracoes/",
    zone: "admin",
    sidebar: false,
    routeKeys: [],
  },
  {
    // Plano "Mídia e uploads por módulo" (Fase 5): antes breadcrumb literal
    // em main.ts, agora entrada do registry (mesmo tratamento de
    // "integrations" acima) — Documentação da API é exclusiva de superadmin
    // (decisão de produto 2026-08-24), não liberável a admin via Perfis de Acesso.
    id: "api-docs",
    title: "Documentação da API",
    path: "/painel/admin/api-docs/",
    zone: "admin",
    breadcrumbParent: "configuracao-section",
    superadminOnly: true,
    sidebar: true,
    icon: "book-open",
    routeKeys: ["api-docs"],
  },
  {
    // Renome de "Mapa do Site" para "Sitemap" (URL mantida) — plano "Mídia e
    // uploads por módulo" (Fase 5). Mesmo tratamento de "api-docs" acima.
    id: "sitemap",
    title: "Sitemap",
    path: "/painel/superadmin/mapa-site/",
    zone: "admin",
    breadcrumbParent: "configuracao-section",
    superadminOnly: true,
    sidebar: true,
    icon: "map",
    routeKeys: ["sitemap"],
  },
  {
    // Crumb intermediário do sub-grupo "Geral" (nav-config.ts) — 3º nível de
    // navegação dentro de "Configurações" (plano "Mídia e uploads por
    // módulo", Fase 5): as 3 páginas nascidas do split da antiga
    // "Customização da Instância" (ADR-042 → Fase 6 do mesmo plano). `path`
    // aponta para a 1ª aba (Instância) — mesmo padrão de
    // "pool-hotelaria-section" (crumb sem página própria, mas com um destino
    // navegável coerente).
    id: "configuracoes-geral-section",
    title: "Geral",
    path: "/painel/superadmin/instancia/",
    zone: "admin",
    breadcrumbParent: "configuracao-section",
    sidebar: false,
    routeKeys: [],
  },
  {
    id: "instance-settings",
    title: "Instância",
    path: "/painel/superadmin/instancia/",
    zone: "admin",
    breadcrumbParent: "configuracoes-geral-section",
    superadminOnly: true,
    sidebar: true,
    launcher: true,
    icon: "building-2",
    routeKeys: ["instance-settings"],
  },
  {
    id: "instance-layout",
    title: "Aparência",
    path: "/painel/superadmin/layout/",
    zone: "admin",
    breadcrumbParent: "configuracoes-geral-section",
    superadminOnly: true,
    sidebar: true,
    icon: "palette",
    routeKeys: ["instance-layout"],
  },
  {
    id: "white-label",
    title: "White-label",
    path: "/painel/superadmin/white-label/",
    zone: "admin",
    breadcrumbParent: "configuracoes-geral-section",
    superadminOnly: true,
    sidebar: true,
    icon: "tag",
    routeKeys: ["white-label"],
  },
  {
    // Crumb intermediário do grupo "Mídia" (nav-config.ts) — plano "Mídia e
    // uploads por módulo" (Fase 5). Mesmo papel de "configuracao-section"/
    // "parametrizacao-section" acima.
    id: "midia-section",
    title: "Mídia",
    path: "/painel/admin/midia/biblioteca/",
    zone: "admin",
    sidebar: false,
    routeKeys: [],
  },
  {
    // Admin (perfil padrão) vê/sobe/apaga por decisão do dono (Fase 1 do
    // plano) — perm concedida ao GroupProfile default via migração de dados,
    // não `superadminOnly`.
    id: "media-library",
    title: "Biblioteca",
    path: "/painel/admin/midia/biblioteca/",
    zone: "admin",
    breadcrumbParent: "midia-section",
    perm: codename("midia.admin.biblioteca", "view"),
    sidebar: true,
    launcher: true,
    icon: "image",
    routeKeys: ["media-library"],
  },
  {
    id: "media-upload",
    title: "Upload",
    path: "/painel/admin/midia/upload/",
    zone: "admin",
    breadcrumbParent: "midia-section",
    perm: codename("midia.admin.biblioteca", "create"),
    sidebar: true,
    icon: "upload",
    routeKeys: ["media-upload"],
  },
  {
    // ADR-014, Fase 3: 1ª entrada fora do cluster "Acessos & Permissões" — do
    // PoC A (só Launcher) à Implementação completa (sidebar + breadcrumb
    // também migrados, fechando a inconsistência de nome que o próprio PoC A
    // encontrou entre Launcher/Sidebar/Breadcrumb).
    id: "integrations",
    title: "Integrações",
    path: "/painel/admin/integracoes/",
    zone: "admin",
    breadcrumbParent: "configuracao-section",
    superadminOnly: true,
    sidebar: true,
    launcher: true,
    icon: "plug",
    routeKeys: ["integracoes", "integracao-form"],
  },
  {
    // Crumb intermediário do grupo "Parametrização" (nav-config.ts, ex-
    // "Cotista") — mesmo papel de "configuracao-section" acima.
    id: "parametrizacao-section",
    title: "Parametrização",
    path: "/painel/admin/empreendimentos/",
    zone: "admin",
    sidebar: false,
    routeKeys: [],
  },
  {
    id: "admin-empreendimentos",
    title: "Empreendimentos",
    path: "/painel/admin/empreendimentos/",
    zone: "admin",
    breadcrumbParent: "parametrizacao-section",
    perm: codename("parametrizacoes.admin.empreendimentos", "view"),
    sidebar: true,
    launcher: true,
    icon: "building-2",
    routeKeys: ["admin-empreendimentos"],
  },
  {
    // Nome unificado: sidebar (nav-config.ts) dizia "Boletos", o card do
    // Launcher (literal) dizia "Config Boletos" — 2 nomes para a mesma tela,
    // exatamente o risco que a Definition of Done desta fase existe para
    // fechar. "Boletos" escolhido por ser o nome já usado no local
    // load-bearing (sidebar), consistente com o estilo dos demais módulos
    // (sem prefixo "Config").
    id: "admin-boletos",
    title: "Boletos",
    path: "/painel/admin/boletos/",
    zone: "admin",
    breadcrumbParent: "parametrizacao-section",
    perm: codename("parametrizacoes.admin.boletos", "view"),
    sidebar: true,
    launcher: true,
    icon: "file-text",
    routeKeys: ["admin-boletos"],
  },
  {
    // Nome unificado: sidebar dizia "Regras eSolution Portal", o card do
    // Launcher (literal) dizia "Regras Portal". Mesmo motivo do item acima —
    // "Regras eSolution Portal" escolhido por ser o nome do local
    // load-bearing (sidebar) e mais preciso (nomeia a integração real).
    id: "admin-portal-regras",
    title: "Regras eSolution Portal",
    path: "/painel/admin/portal-regras/",
    zone: "admin",
    breadcrumbParent: "parametrizacao-section",
    perm: codename("parametrizacoes.admin.portal-regras", "view"),
    sidebar: true,
    launcher: true,
    icon: "edit-3",
    routeKeys: ["admin-portal-regras"],
  },
  {
    // Extraída de "Documentos" (main.ts, `/painel/admin/documentos/`), que
    // cobria Documentos e Categorias em 2 abas sob um guard resource-any
    // (Card 868m1ja6b) — permissão própria, `breadcrumbParent` no mesmo
    // grupo dos demais `parametrizacoes.admin.*` (Empreendimentos/Boletos/
    // Regras eSolution Portal).
    id: "admin-documentos-categorias",
    title: "Parametrização de Documentos",
    path: "/painel/admin/documentos-categorias/",
    zone: "admin",
    breadcrumbParent: "parametrizacao-section",
    perm: codename("parametrizacoes.admin.documentos-categorias", "view"),
    sidebar: true,
    launcher: true,
    icon: "settings",
    routeKeys: ["admin-documentos-categorias"],
  },
  {
    // Crumb intermediário do grupo "Pool de Hotelaria" (nav-config.ts,
    // userNavConfig) — mesmo papel de "access-permissions-section"/
    // "configuracao-section"/"parametrizacao-section" acima, na zona user.
    id: "pool-hotelaria-section",
    title: "Pool de Hotelaria",
    path: "/app/pool",
    zone: "user",
    sidebar: false,
    routeKeys: [],
  },
  {
    id: "pool-visao-geral",
    title: "Visão Geral",
    path: "/app/pool",
    zone: "user",
    breadcrumbParent: "pool-hotelaria-section",
    perm: codename("hotelaria.user.pool-hoteleiro", "view"),
    sidebar: true,
    icon: "booking",
    routeKeys: ["user-pool"],
  },
  {
    id: "pool-cotas-unidades",
    title: "Cotas e Unidades",
    path: "/app/cotas",
    zone: "user",
    breadcrumbParent: "pool-hotelaria-section",
    perm: codename("condominio.user.cotas", "view"),
    sidebar: true,
    icon: "booking",
    routeKeys: ["user-cotas"],
  },
  {
    id: "pool-ocupacao",
    title: "Ocupação",
    path: "/app/ocupacao",
    zone: "user",
    breadcrumbParent: "pool-hotelaria-section",
    perm: codename("hotelaria.user.ocupacao", "view"),
    sidebar: true,
    icon: "booking",
    routeKeys: ["user-ocupacao"],
  },
];

export function resolvePath(entry: NavEntry, ctx: RoleCtx): string {
  return typeof entry.path === "function" ? entry.path(ctx) : entry.path;
}

export function getNavEntry(id: string): NavEntry {
  const entry = navRegistry.find((e) => e.id === id);
  if (!entry) throw new Error(`nav-registry: id desconhecido "${id}"`);
  return entry;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * Monta o breadcrumb completo a partir de um id do registry, subindo a
 * cadeia de `breadcrumbParent`. `extra` anexa segmentos finais dinâmicos
 * (ex.: rota de edição — o item do registry vira um link intermediário e
 * "Editar"/o nome dinâmico é o leaf).
 */
export function buildBreadcrumb(id: string, ctx: RoleCtx, extra: BreadcrumbItem[] = []): BreadcrumbItem[] {
  const entry = getNavEntry(id);
  const chain: NavEntry[] = [];
  let current: NavEntry | undefined = entry;
  while (current) {
    chain.unshift(current);
    current = current.breadcrumbParent ? navRegistry.find((e) => e.id === current!.breadcrumbParent) : undefined;
  }
  const idCrumbs: BreadcrumbItem[] = chain.map((e, i) => {
    const isLastOfChain = i === chain.length - 1;
    const hasHref = !isLastOfChain || extra.length > 0;
    return { label: e.title, href: hasHref ? resolvePath(e, ctx) : undefined };
  });
  const extraCrumbs: BreadcrumbItem[] = extra.map((c, i) => ({
    label: c.label,
    href: i === extra.length - 1 ? undefined : c.href,
  }));
  return [ROOTS[entry.zone], ...idCrumbs, ...extraCrumbs];
}